import { GridParams, WindGeoJSON } from '../types';

/**
 * Round a number to a specific number of decimal places
 */
function roundToPrecision(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/**
 * Calculate the median of an array of numbers
 */
function median(values: number[]): number {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }
  return sorted[mid];
}

/**
 * Detects grid parameters from a GeoJSON FeatureCollection of points
 *
 * @param geoJSON - GeoJSON FeatureCollection with Point geometries
 * @returns GridParams object with grid dimensions and spacing
 * @throws Error if data is not on a regular grid
 */
export function detectGridParameters(geoJSON: WindGeoJSON): GridParams {
  const features = geoJSON.features;

  if (!features || features.length === 0) {
    throw new Error('GeoJSON FeatureCollection is empty');
  }

  // Extract unique coordinates (rounded to avoid floating-point precision issues)
  const latsSet = new Set<number>();
  const lonsSet = new Set<number>();

  features.forEach(feature => {
    if (feature.geometry.type !== 'Point') {
      throw new Error('Only Point geometries are supported');
    }

    const [lon, lat] = feature.geometry.coordinates;
    latsSet.add(roundToPrecision(lat, 4));
    lonsSet.add(roundToPrecision(lon, 4));
  });

  // Sort coordinates
  const sortedLats = Array.from(latsSet).sort((a, b) => b - a); // Descending (north to south)
  const sortedLons = Array.from(lonsSet).sort((a, b) => a - b); // Ascending (west to east)

  if (sortedLats.length < 2 || sortedLons.length < 2) {
    throw new Error('Grid must have at least 2 points in each dimension');
  }

  // Calculate spacing using all consecutive differences
  const dxValues: number[] = [];
  const dyValues: number[] = [];

  for (let i = 1; i < sortedLons.length; i++) {
    dxValues.push(sortedLons[i] - sortedLons[i - 1]);
  }

  for (let i = 1; i < sortedLats.length; i++) {
    dyValues.push(Math.abs(sortedLats[i] - sortedLats[i - 1]));
  }

  // Use median to handle potential floating-point precision issues
  const dx = roundToPrecision(median(dxValues), 4);
  const dy = roundToPrecision(median(dyValues), 4);

  // Validate regularity (all spacing should be within tolerance)
  const tolerance = 0.001; // 0.001 degree tolerance (~111 meters at equator)

  const isRegularX = dxValues.every(v => Math.abs(v - dx) < tolerance);
  const isRegularY = dyValues.every(v => Math.abs(v - dy) < tolerance);

  if (!isRegularX || !isRegularY) {
    console.warn('[GridDetector] Irregularities detected in grid spacing');
    console.warn('[GridDetector] dx values:', dxValues);
    console.warn('[GridDetector] dy values:', dyValues);
    console.warn('[GridDetector] median dx:', dx, 'median dy:', dy);

    // Check if variation is small enough to proceed
    const maxDxVariation = Math.max(...dxValues.map(v => Math.abs(v - dx)));
    const maxDyVariation = Math.max(...dyValues.map(v => Math.abs(v - dy)));

    if (maxDxVariation > 0.01 || maxDyVariation > 0.01) {
      throw new Error(
        `Data is not on a regular grid. ` +
        `Maximum longitude spacing variation: ${maxDxVariation.toFixed(4)}°, ` +
        `Maximum latitude spacing variation: ${maxDyVariation.toFixed(4)}°`
      );
    }

    console.warn('[GridDetector] Proceeding with median spacing despite minor irregularities');
  }

  // Validate expected number of points
  const expectedPoints = sortedLats.length * sortedLons.length;
  if (features.length !== expectedPoints) {
    console.warn(
      `[GridDetector] Expected ${expectedPoints} points (${sortedLons.length} × ${sortedLats.length}), ` +
      `but got ${features.length}. Some grid cells may be missing.`
    );
  }

  return {
    nx: sortedLons.length,
    ny: sortedLats.length,
    dx,
    dy,
    la1: sortedLats[0],  // Northern-most latitude
    lo1: sortedLons[0],  // Western-most longitude
    la2: sortedLats[sortedLats.length - 1],  // Southern-most latitude
    lo2: sortedLons[sortedLons.length - 1],  // Eastern-most longitude
  };
}

/**
 * Utility function to round coordinates for consistent comparison
 */
export { roundToPrecision };
